import athenaConfig from '../config';
import {
  ANET_USER_PREFIX_SUPPORT_ENV_MAP,
  AppType,
  APP_TYPES,
  ATHENISTA_USER_DOMAIN,
  CREDENTIAL_TYPES,
  CredentialType,
  FUZZY_ENV_MAPPING,
  NON_FUZZY_ANET_USER_DOMAIN,
  SCIM_ANET_USER_DOMAIN_PATTERN
} from '../constants';
import { getUsername } from './widget-facade';

export function detectCredentialType(input: string): CredentialType {
  if (!input) return CREDENTIAL_TYPES.UNKNOWN;

  const trimmed = input.trim();
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!trimmed.includes('@')) {
    return CREDENTIAL_TYPES.ANET_USERNAME;
  }

  // Any *.managedidentity.platform.athenahealth.com variant (prod, preview,
  // sandbox, etc.) is an already-transformed AthenaNet username.
  if (trimmed.includes(NON_FUZZY_ANET_USER_DOMAIN)) {
    return CREDENTIAL_TYPES.NAMESPACED_ANET_USERNAME;
  }

  if (EMAIL_REGEX.test(trimmed)) {
    return CREDENTIAL_TYPES.EMAIL;
  }

  return CREDENTIAL_TYPES.UNKNOWN;
}

export function isAnetUser() {
  const username = getUsername();
  return username?.includes(NON_FUZZY_ANET_USER_DOMAIN);
}

export function getAnetUsername() {
  return isAnetUser() ? getUsername().split('@')[0] : '';
}

export function isAthenista() {
  return getUserType() === APP_TYPES.ATHENISTA;
}

export function shouldDisplayResetAccountLink() {
  const { appType, hideResetAccountOption } = global.settings;
  const shouldAppTypeDisplayResetAccountLink =
    appType ? ![ APP_TYPES.ATHENISTA, APP_TYPES.ATHENANET_USER ].includes(appType) : true;
  return !hideResetAccountOption && !isAnetUser() && !isAthenista() && shouldAppTypeDisplayResetAccountLink;
}

export function getPatientUserNamespace() {
  const { supportEnv, appType } = global.settings;
  if (supportEnv && appType === APP_TYPES.PATIENT) {
    return FUZZY_ENV_MAPPING[supportEnv].userNamespace;
  }
}

export function getAnetDb(): string | undefined  {
  if (athenaConfig.featureFlags.enableAnetDbNamespace) {
    return new URLSearchParams(window.location.search).get('anet_db') || undefined;
  }
}

export function getAnetUserDomain(username: string) {
  const userPrefix = username.split('-')[0];
  const { supportEnv: appSupportEnv, appType } = global.settings;
  if (userPrefix) {
    const userSupportEnv = ANET_USER_PREFIX_SUPPORT_ENV_MAP[userPrefix];
    if (appSupportEnv && userSupportEnv === appSupportEnv && appType === APP_TYPES.ATHENANET_USER) {
      return FUZZY_ENV_MAPPING[appSupportEnv].anetUserDomain;
    }
  }
  if (appType === APP_TYPES.ATHENANET_USER || appType === APP_TYPES.ADJACENT_AONE_ATHENISTA_AUTH) {
    if (athenaConfig.featureFlags.enableAnetDbNamespace) {
      const anetDb = getAnetDb();
      if (!anetDb && athenaConfig.featureFlags.enableDefaultNamespace && athenaConfig.defaultNamespace) {
        return `${athenaConfig.defaultNamespace}.${NON_FUZZY_ANET_USER_DOMAIN}`;
      }
      if (anetDb) {
        return `${anetDb}.${NON_FUZZY_ANET_USER_DOMAIN}`;
      }
    }
  }
  return NON_FUZZY_ANET_USER_DOMAIN;
}

export function transformUsername(username: string, _operation: string) {
  //non-anet users
  if (username.indexOf('@') > -1) {
    const patientUserNamespace = getPatientUserNamespace();
    if (patientUserNamespace && !username.endsWith(patientUserNamespace)) {
      return `${username}${patientUserNamespace}`;
    }
    return username;
  }
  else {
    return username + '@' + getAnetUserDomain(username);
  }
}

// Classifies the current user as athenaNet, athenista, or patient/adjacent-services based on login domain.
// Returns undefined if the username isn't known yet (e.g. before the widget has initialized,
// or before the user has entered their credentials) rather than guessing patient/adjacent.
export function getUserType(): AppType | undefined {
  let username;
  try {
    username = getUsername();
  }
  catch (error) {
    username = undefined;
  }
  if (!username) {
    return undefined;
  }
  const lowerUsername = username.toLowerCase();
  if (lowerUsername.endsWith(NON_FUZZY_ANET_USER_DOMAIN) || SCIM_ANET_USER_DOMAIN_PATTERN.test(lowerUsername)) {
    return APP_TYPES.ATHENANET_USER;
  }
  if (lowerUsername.endsWith(ATHENISTA_USER_DOMAIN)) {
    return APP_TYPES.ATHENISTA;
  }
  return APP_TYPES.PATIENT_OR_ADJACENT;
}

export function isSupportEnvUser() {
  const username = getUsername();
  const userPrefix = username.split('-')[0];
  return ANET_USER_PREFIX_SUPPORT_ENV_MAP[userPrefix];
}

export function isProdUserInSupport() {
  if (athenaConfig.shouldBlockPasswordUpdateForProdUser) {
    return !isSupportEnvUser();
  }
  return false;
}
